import type { ReactNode } from 'react';
import React, { useRef, useState } from 'react';

import { Select } from '@aline/common';
import { link } from '@aline/routes';
import { ButtonBase, Popover, Typography } from '@mui/material';
import { LEFT_NAV_BACKGROUND_COLOR, NameDialog, NameDialogVariant } from 'src/components';
import { useAccessGuard, useTierWorkflowCapGuard } from 'src/components/auth';
import { DocumentFileIcon } from 'src/components/icons/DocumentFileIcon';
import { PlusIcon } from 'src/components/icons/PlusIcon';
import { SigningIcon } from 'src/components/icons/SigningIcon';
import { StartFromTemplateIcon } from 'src/components/icons/StartFromTemplateIcon';
import { CreateWorkflowDialog } from 'src/components/workflow/board/CreateWorkflowDialog';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { openLink } from 'src/helpers';
import { useCreateAndOpenNewDocument } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  actionButtonContainer: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    padding: 0,
    width: '100%',
  },
  collapsedButton: {
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
    },
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1),
    position: 'relative',
    transition: theme.transitions.create(['background-color', 'box-shadow'], {
      duration: theme.transitions.duration.shortest,
    }),
  },
  iconContainer: {
    alignItems: 'center',
    backgroundColor: '#F1F1F1',
    borderRadius: 3,
    display: 'flex',
    gap: theme.spacing(1),
    height: 48,
    justifyContent: 'center',
    width: 48,
  },
  menu: {
    borderRadius: 16,
    marginTop: theme.spacing(0.5),
    maxWidth: 420,
    padding: theme.spacing(2, 0),
  },
  plusCircleIcon: {
    transition: theme.transitions.create(['filter'], {
      duration: theme.transitions.duration.shortest,
    }),
  },
  textGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

export function EntrypointButton(): JSX.Element | null {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { createAndOpenNewDocument } = useCreateAndOpenNewDocument();
  const [entrypointMenuOpen, setEntrypointMenuOpen] = useState(false);
  const [nameDialogOpen, setNameDialogOpen] = useState<boolean>(false);
  const [createWorkflowDialogOpen, setCreateWorkflowDialogOpen] = useState(false);
  const safeOpenCreateWorkflowDialog = useTierWorkflowCapGuard(() =>
    setCreateWorkflowDialogOpen(true)
  );
  const handleStartWorkflow = useAccessGuard({
    callback: () => safeOpenCreateWorkflowDialog(),
    minRole: Apollo.OrgRole.Basic,
  });
  const handleLaunchTemplate = useAccessGuard({
    callback: () => openLink(link.QuickStartLaunchTemplateIndex(), { title: 'Launch Template' }),
    minRole: Apollo.OrgRole.Basic,
  });
  const handleCreateDocument = useAccessGuard({
    callback: () => setNameDialogOpen(true),
    minRole: Apollo.OrgRole.Basic,
  });

  const buttons = [
    {
      Icon: SigningIcon,
      backgroundColor: '#F4F1D3',
      dataCy: Select.leftNav.entrypoint.newWorkflowButton,
      iconStroke: '#18B55E',
      onClick: handleStartWorkflow,
      subtext: 'Start a new workflow to sign or review documents with AI.',
      title: 'Start new workflow',
    },
    {
      Icon: StartFromTemplateIcon,
      backgroundColor: '#D6DCFF',
      dataCy: Select.leftNav.entrypoint.newTemplateButton,
      iconStroke: null,
      onClick: handleLaunchTemplate,
      subtext: 'Use a template to start a new signing or review workflow.',
      title: 'Launch template',
    },
    {
      Icon: DocumentFileIcon,
      backgroundColor: '#FFFFFF',
      dataCy: Select.leftNav.entrypoint.createDocumentButton,
      iconStroke: null,
      onClick: handleCreateDocument,
      subtext: 'Create a blank document.',
      title: 'New document',
    },
  ] as const;

  return (
    <>
      <div className={classes.actionButtonContainer}>
        <ButtonBase
          ref={buttonRef}
          className={classes.collapsedButton}
          data-cy={Select.leftNav.entrypoint.createNewButton}
          onClick={() => setEntrypointMenuOpen(!entrypointMenuOpen)}
        >
          <PlusIcon
            className={classes.plusCircleIcon}
            height={24}
            stroke={LEFT_NAV_BACKGROUND_COLOR}
            width={24}
          />
        </ButtonBase>
      </div>
      <Popover
        anchorEl={buttonRef.current}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        classes={{
          paper: classes.menu,
        }}
        open={entrypointMenuOpen}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        onClose={() => setEntrypointMenuOpen(false)}
      >
        {buttons.map(({ Icon, dataCy, iconStroke, onClick, subtext, title }) => (
          <EntrypointMenuOption
            key={title}
            dataCy={dataCy}
            description={subtext}
            title={title}
            onClick={() => {
              onClick();
              setEntrypointMenuOpen(false);
            }}
          >
            <Icon fill={iconStroke ?? undefined} />
          </EntrypointMenuOption>
        ))}
      </Popover>
      <CreateWorkflowDialog
        open={createWorkflowDialogOpen}
        onClose={() => setCreateWorkflowDialogOpen(false)}
      />
      <NameDialog
        handleClose={() => setNameDialogOpen(false)}
        open={nameDialogOpen}
        placeholder="Add a title for your document..."
        variant={NameDialogVariant.CreateDocument}
        onFormikSubmit={async name => {
          await createAndOpenNewDocument({
            afterOpen: () => setNameDialogOpen(false),
            name,
            reportId: currentUser?.myRepositoryId,
            variant: Apollo.DocumentVariant.Document,
          });
        }}
      />
    </>
  );
}

const useEntrypointMenuOptionStyles = makeStyles()(theme => ({
  iconContainer: {
    alignItems: 'center',
    backgroundColor: theme.appColors.background2,
    borderRadius: 8,
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(1.25),
  },
  option: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
    },
    alignItems: 'center',
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  textGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

interface EntrypointMenuOptionProps {
  children: ReactNode;
  description: string;
  onClick: () => void;
  title: string;
  color?: string;
  dataCy?: string;
}

export function EntrypointMenuOption({
  children,
  dataCy,
  description,
  onClick,
  title,
}: EntrypointMenuOptionProps) {
  const { classes } = useEntrypointMenuOptionStyles();

  return (
    <div className={classes.option} data-cy={dataCy} onClick={onClick}>
      <div className={classes.iconContainer}>{children}</div>
      <div className={classes.textGroup}>
        <Typography variant="subtitle2">{title}</Typography>
        <Typography color="textSecondary">{description}</Typography>
      </div>
    </div>
  );
}
